import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { api } from '@/api/repository/axios';
import type { InstructorCoupon, InstructorCouponPayload } from '@/api/repository/types/types';
import { CouponFooterButton, CouponList, CouponNotes, CouponRow, CouponStatusTone, CouponTag } from '@/components/domain/coupon/CouponRow';
import { couponAmountSub, couponAmountText, errorMessageOf, shortUntil, unwrapList } from '@/components/domain/coupon/couponFormat';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';
import { designSystem } from '@/styles/theme';
import { FORM_COLOR } from '@/pages/Seminar/components/form/formStyles';
import InstructorCouponDialog, { CouponDraft, MAX_DRAFT_COUPONS, validateCouponPayload } from './InstructorCouponDialog';

/*
 * 강사 세미나 폼 "쿠폰" 섹션 (시안 r4-instructor-form) — 커리어 유료 세미나.
 * 수정 화면(seminarId 있음): 서버 쿠폰 목록 — 이름 · 코드 · 기한 · 받음 N/최대 · 사용 · 복원 여부 · 중지/재개. "수정"으로 고친다.
 * 신규 등록(seminarId 없음): 초안 목록 — "저장하면 발급돼요" · 수정 · 삭제. 등록 요청 coupons[] 로 함께 보낸다(공유 링크는 저장 후).
 * 수정 화면에서 모집 마감·참가비를 바꾸고 아직 저장 전이면(deferred) 서버는 저장된 값으로 검사하므로 바로 보내지 않는다 —
 * 새 쿠폰은 초안("저장하면 발급돼요"), 기존 쿠폰 고치기는 보류("저장하면 바뀌어요")로 담고, 세미나를 저장할 때 폼이 반영한다.
 * 서버 API 가 아직 없으면 빈 목록으로 보인다.
 */

const PRIMARY: string = designSystem.colors.primary;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const HeadLeft = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;
  gap: 6px 8px;
  font-size: 0.95rem;
  font-weight: 700;
  color: #374151;
`;

const BurdenPill = styled.span`
  word-break: keep-all;
  padding: 2px 9px;
  border-radius: 999px;
  background: #fff4e6;
  color: #c2410c;
  font-size: 12px;
  font-weight: 800;
`;

const CreateButton = styled.button`
  height: 40px;
  padding: 0 14px;
  border: 1.5px solid ${PRIMARY};
  border-radius: 12px;
  background: #fff;
  color: ${FORM_COLOR.primaryDeep};
  font-family: inherit;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    border-color: #e5e7eb;
    color: #9ca3af;
    cursor: default;
  }
`;

const Empty = styled.div`
  padding: 14px;
  border: 1px dashed #e5e7eb;
  border-radius: 12px;
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
`;

const LockedLine = styled.div`
  font-weight: 700;
  color: #b45309;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: ${FORM_COLOR.danger};
  white-space: pre-line;
`;

/** 초안 한 줄 설명 — 기한 · 최대 수량 · 복원 여부 · 코드만 */
const draftMetaLine = (p: InstructorCouponPayload, recruitmentEnd?: string | null): string => {
  const parts: string[] = [];
  const until: string = shortUntil(p.endsAt ?? recruitmentEnd);
  if (until) parts.push(until);
  parts.push(p.maxIssues ? `최대 ${p.maxIssues}장` : '수량 무제한');
  parts.push(p.restoreOnRefund ? '환불 시 복원' : '환불 시 복원 안 함');
  if (p.distribution === 'CODE') parts.push('코드만');
  return parts.join(' · ');
};

/** 초안이 지금 폼 값(참가비·모집 마감)으로도 발급될 수 있는지 — 문제 있으면 문구 */
export const draftProblemOf = (
  draft: CouponDraft,
  drafts: CouponDraft[],
  listPrice: number,
  recruitmentEnd?: string | null,
): string | null =>
  validateCouponPayload(draft.payload, {
    listPrice,
    recruitmentEnd,
    isNew: true,
    takenCodes: drafts.filter((d: CouponDraft) => d.key !== draft.key).map((d: CouponDraft) => d.payload.code ?? ''),
  });

/** 초안 중 첫 문제 — 등록 전 확인용("쿠폰 이름: 문구") */
export const firstDraftProblem = (drafts: CouponDraft[], listPrice: number, recruitmentEnd?: string | null): string | null => {
  for (const draft of drafts) {
    const problem: string | null = draftProblemOf(draft, drafts, listPrice, recruitmentEnd);
    if (problem) return `'${draft.payload.name}' 쿠폰: ${problem}`;
  }
  return null;
};

let draftSeq: number = 0;
const nextDraftKey = (): string => {
  draftSeq += 1;
  return `draft-${Date.now()}-${draftSeq}`;
};

/** 수정 화면에서 저장 전 담아 둔 기존 쿠폰 고치기 — update 를 서버에 보내고, payload 는 목록·다시 고치기에 쓴다 */
export interface CouponPendingUpdate {
  id: number;
  update: Partial<InstructorCouponPayload>;
  payload: InstructorCouponPayload;
  /** 서버에 저장된 원래 끝나는 날 — 끝나는 날을 바꿨는지 판단(서버 규칙과 같게) */
  originalEndsAt: string | null;
  /** 받은 사람이 있어 이름·기간·수량만 바꿀 수 있는 쿠폰 */
  valueLocked: boolean;
}

/** 보류 고치기가 지금 폼 값(참가비·모집 마감)으로도 저장될 수 있는지 — 문제 있으면 문구 */
export const pendingUpdateProblem = (pending: CouponPendingUpdate, listPrice: number, recruitmentEnd?: string | null): string | null =>
  validateCouponPayload(pending.payload, {
    listPrice,
    recruitmentEnd,
    valueLocked: pending.valueLocked,
    isNew: false,
    originalEndsAt: pending.originalEndsAt,
  });

/** 보류 중인 고치기를 얹은 쿠폰(다시 고칠 때 창에 이 값으로 연다). 서버 미리보기는 옛 값 기준이라 뺀다 */
const withPending = (coupon: InstructorCoupon, pending?: CouponPendingUpdate): InstructorCoupon =>
  pending
    ? {
      ...coupon,
      ...pending.payload,
      maxDiscountAmount: pending.payload.maxDiscountAmount ?? null,
      maxIssues: pending.payload.maxIssues ?? null,
      code: pending.payload.code ?? coupon.code,
      startsAt: pending.payload.startsAt ?? coupon.startsAt,
      endsAt: pending.payload.endsAt ?? coupon.endsAt,
      previewPrice: undefined,
      settlementBasis: undefined,
      expectedPayout: undefined,
    }
    : coupon;

/** 명치사가 만든 내 부담·분담 쿠폰 — "명치사 발급 · 분담 · 명치사 40 : 강사 60"(어드민 표기와 같은 순서) */
const issuerLine = (c: InstructorCoupon): string => {
  if (c.burdenType === 'SPLIT' && typeof c.instructorShareRate === 'number') {
    const mine: number = Math.round(c.instructorShareRate * 100);
    return `명치사 발급 · 분담 · 명치사 ${100 - mine} : 강사 ${mine}`;
  }
  return `명치사 발급 · ${c.burdenLabel ?? '강사'} 부담`;
};

const metaLine = (c: InstructorCoupon): string => {
  const parts: string[] = [];
  const until: string = shortUntil(c.endsAt);
  if (until) parts.push(until);
  parts.push(`받음 ${c.issuedCount}/${c.maxIssues ?? '무제한'}`);
  parts.push(`사용 ${c.usedCount}`);
  parts.push(c.restoreOnRefund ? '환불 시 복원' : '환불 시 복원 안 함');
  if (c.distribution === 'CODE') parts.push('코드만');
  return parts.join(' · ');
};

/** 결제에 쓰인 쿠폰 — 중지·재개만 된다. 서버 editable 이 false 일 때만(필드 없으면 기존 동작) */
const isPaymentLocked = (c: InstructorCoupon): boolean => c.editable === false;

interface InstructorCouponSectionProps {
  /** 저장된 세미나 id — 없으면(신규 등록) 초안 모드 */
  seminarId?: string;
  listPrice: number;
  recruitmentEnd?: string | null;
  /** 초안 모드: 등록 요청에 함께 보낼 쿠폰 초안(폼이 들고 있다) */
  drafts?: CouponDraft[];
  onDraftsChange?: (drafts: CouponDraft[]) => void;
  /** 수정 화면: 모집 마감·참가비가 저장된 값과 달라 쿠폰을 바로 저장하지 않고 담아 둔다 */
  deferred?: boolean;
  /** 수정 화면: 저장 전 담아 둔 기존 쿠폰 고치기 */
  pendingUpdates?: CouponPendingUpdate[];
  onPendingUpdatesChange?: (next: CouponPendingUpdate[]) => void;
  /** 바뀌면 서버 목록을 다시 읽는다(세미나 저장 뒤 쿠폰을 반영했을 때) */
  reloadKey?: number;
}

const InstructorCouponSection: React.FC<InstructorCouponSectionProps> = ({
  seminarId,
  listPrice,
  recruitmentEnd,
  drafts = [],
  onDraftsChange,
  deferred = false,
  pendingUpdates = [],
  onPendingUpdatesChange,
  reloadKey = 0,
}) => {
  const draftMode: boolean = !seminarId;
  const [editingDraft, setEditingDraft] = useState<CouponDraft | null>(null);
  const [coupons, setCoupons] = useState<InstructorCoupon[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<InstructorCoupon | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async (): Promise<void> => {
    if (!seminarId) return;
    try {
      const res = await api.coupon.instructorList(seminarId);
      setCoupons(res?.code === 0 ? unwrapList<InstructorCoupon>(res.result) : []);
    } catch {
      setCoupons([]);
    } finally {
      setLoaded(true);
    }
  }, [seminarId]);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  const pendingOf = (couponId: number): CouponPendingUpdate | undefined =>
    pendingUpdates.find((u: CouponPendingUpdate) => u.id === couponId);

  const savePendingUpdate = (update: Partial<InstructorCouponPayload>, payload: InstructorCouponPayload): void => {
    if (!editing) return;
    const original: InstructorCoupon | undefined = coupons.find((c: InstructorCoupon) => c.id === editing.id);
    const item: CouponPendingUpdate = {
      id: editing.id,
      update,
      payload,
      originalEndsAt: original?.endsAt ?? null,
      valueLocked: original?.editableValue === false,
    };
    onPendingUpdatesChange?.([...pendingUpdates.filter((u: CouponPendingUpdate) => u.id !== editing.id), item]);
    setDialogOpen(false);
    setEditing(null);
  };

  const togglePause = async (coupon: InstructorCoupon): Promise<void> => {
    setTogglingId(coupon.id);
    setError('');
    try {
      const res = coupon.status === 'PAUSED'
        ? await api.coupon.instructorResume(coupon.id)
        : await api.coupon.instructorPause(coupon.id);
      if (res?.code !== 0) setError(res?.message || '쿠폰 상태를 바꾸지 못했어요.');
      await load();
    } catch (e: unknown) {
      setError(errorMessageOf(e, '쿠폰 상태를 바꾸지 못했어요.'));
    } finally {
      setTogglingId(null);
    }
  };

  /** 두 번째 버튼 — 진행 중 [사용 중지] · 중지됨 [다시 쓰기] · 만료 [만료](못 누름). 명치사 쿠폰은 없음 */
  const toggleButtonOf = (coupon: InstructorCoupon): CouponFooterButton | null => {
    if (coupon.mine === false) return null; // 명치사가 만든 쿠폰은 읽기 전용
    if (coupon.status === 'EXPIRED') return { label: '만료', disabled: true };
    const busy: boolean = togglingId === coupon.id;
    const label: string = coupon.status === 'PAUSED' ? '다시 쓰기' : '사용 중지';
    return {
      label: busy ? <ButtonSpinner /> : label,
      disabled: togglingId !== null,
      onClick: () => void togglePause(coupon),
      ariaLabel: `${coupon.name} 쿠폰 ${label}`,
    };
  };

  const statusOf = (coupon: InstructorCoupon, pending?: CouponPendingUpdate): { text: string; tone: CouponStatusTone } => {
    if (pending) return { text: '저장하면 바뀌어요', tone: 'pending' };
    if (coupon.status === 'PAUSED') return { text: '중지됨', tone: 'paused' };
    if (coupon.status === 'EXPIRED') return { text: '만료', tone: 'paused' };
    return { text: '진행 중', tone: 'active' };
  };

  const canCreate: boolean = listPrice > 0 && drafts.length < MAX_DRAFT_COUPONS;

  const openCreate = (): void => {
    setEditing(null);
    setEditingDraft(null);
    setDialogOpen(true);
  };

  const saveDraft = (payload: InstructorCouponPayload): void => {
    const next: CouponDraft[] = editingDraft
      ? drafts.map((d: CouponDraft) => (d.key === editingDraft.key ? { ...d, payload } : d))
      : [...drafts, { key: nextDraftKey(), payload }];
    onDraftsChange?.(next);
    setDialogOpen(false);
    setEditingDraft(null);
  };

  const removeDraft = (key: string): void => {
    onDraftsChange?.(drafts.filter((d: CouponDraft) => d.key !== key));
  };
  // 초안 줄 — 새 등록 초안과 수정 화면에서 저장 전 담아 둔 새 쿠폰
  const draftRows: React.ReactNode = drafts.map((draft: CouponDraft) => {
    const problem: string | null = draftProblemOf(draft, drafts, listPrice, recruitmentEnd);
    return (
      <CouponRow
        key={draft.key}
        amount={couponAmountText(draft.payload)}
        amountSub={couponAmountSub(draft.payload)}
        title={draft.payload.name}
        tags={draft.payload.code ? [{ text: draft.payload.code, tone: 'code' }] : []}
        tagsFirst
        status={{ text: '저장하면 발급돼요', tone: 'pending' }}
        description={
          <>
            <div>{draftMetaLine(draft.payload, recruitmentEnd)}</div>
            {problem && <ErrorText role="alert">{problem}</ErrorText>}
          </>
        }
        footer={[
          {
            label: '수정',
            onClick: () => { setEditing(null); setEditingDraft(draft); setDialogOpen(true); },
            ariaLabel: `${draft.payload.name} 쿠폰 수정`,
          },
          { label: '삭제', danger: true, onClick: () => removeDraft(draft.key), ariaLabel: `${draft.payload.name} 쿠폰 삭제` },
        ]}
      />
    );
  });

  // 공유 링크는 내가 만든 받기형·진행 중 쿠폰만 — 명치사 쿠폰·코드 전용 쿠폰의 코드는 절대 노출하지 않는다
  const shareCode: string | undefined = coupons.find((c: InstructorCoupon) =>
    c.mine !== false && c.distribution === 'CLAIM' && c.status === 'ACTIVE')?.code;

  return (
    <Wrap>
      <Head>
        <HeadLeft>
          쿠폰{' '}
          <BurdenPill>
            {coupons.some((c: InstructorCoupon) => c.mine === false)
              ? '내 쿠폰은 강사 부담 · 명치사 쿠폰은 표시된 분담 기준'
              : '할인은 강사 부담'}
          </BurdenPill>
        </HeadLeft>
        <CreateButton type="button" disabled={!canCreate} onClick={openCreate}>
          쿠폰 만들기
        </CreateButton>
      </Head>

      {draftMode ? (
        drafts.length === 0 ? (
          <Empty>만든 쿠폰은 세미나를 등록할 때 함께 발급돼요.</Empty>
        ) : (
          <CouponList>{draftRows}</CouponList>
        )
      ) : !loaded ? null : coupons.length === 0 && drafts.length === 0 ? (
        <Empty>아직 만든 쿠폰이 없어요.</Empty>
      ) : (
        <CouponList>
          {draftRows}
          {coupons.map((coupon: InstructorCoupon) => {
            const pending: CouponPendingUpdate | undefined = pendingOf(coupon.id);
            const name: string = pending ? pending.payload.name : coupon.name;
            // 명치사가 만든 쿠폰의 코드는 강사에게도 보이지 않는다
            const tags: CouponTag[] = coupon.mine === false ? [] : [{ text: coupon.code, tone: 'code' }];
            const toggle: CouponFooterButton | null = toggleButtonOf(coupon);
            const footer: CouponFooterButton[] = coupon.mine === false
              ? []
              : [
                // 결제에 쓰인 쿠폰은 고칠 수 없다 — [보기](읽기 전용)로만 연다
                {
                  label: isPaymentLocked(coupon) ? '보기' : '수정',
                  onClick: () => { setEditingDraft(null); setEditing(withPending(coupon, pending)); setDialogOpen(true); },
                  ariaLabel: `${name} 쿠폰 ${isPaymentLocked(coupon) ? '보기' : '수정'}`,
                },
                ...(pending
                  ? [{
                    label: '바꾼 내용 취소',
                    onClick: () => onPendingUpdatesChange?.(pendingUpdates.filter((u: CouponPendingUpdate) => u.id !== coupon.id)),
                    ariaLabel: `${name} 쿠폰 바꾼 내용 취소`,
                  }]
                  : []),
                ...(toggle ? [toggle] : []),
              ];
            return (
              <CouponRow
                key={coupon.id}
                amount={couponAmountText(coupon)}
                amountSub={couponAmountSub(coupon)}
                // 보류 중인 고치기가 있으면 바뀔 이름으로
                title={name}
                tags={tags}
                tagsFirst
                status={statusOf(coupon, pending)}
                description={
                  <>
                    {coupon.mine === false && <div>{issuerLine(coupon)}</div>}
                    <div>{pending ? draftMetaLine(pending.payload, recruitmentEnd) : metaLine(coupon)}</div>
                    {pending && pendingUpdateProblem(pending, listPrice, recruitmentEnd) && (
                      <ErrorText role="alert">{pendingUpdateProblem(pending, listPrice, recruitmentEnd)}</ErrorText>
                    )}
                    {isPaymentLocked(coupon) && (
                      <LockedLine>결제 {coupon.usedCount}건 사용 · 중지 후 새로 만들기</LockedLine>
                    )}
                  </>
                }
                footer={footer.length > 0 ? footer : undefined}
                muted={coupon.status !== 'ACTIVE'}
              />
            );
          })}
        </CouponList>
      )}

      {error && <ErrorText role="alert">{error}</ErrorText>}

      <CouponNotes
        lines={[
          '받기 쿠폰은 세미나 상세에 보이고 참가자가 직접 받아요.',
          '코드만 쿠폰은 코드를 아는 사람만 쓸 수 있어요.',
          ...(draftMode
            ? [`한 세미나에 쿠폰은 ${MAX_DRAFT_COUPONS}개까지 함께 만들 수 있어요.`, '공유 링크는 등록한 뒤 수정 화면에서 볼 수 있어요.']
            : shareCode ? [`공유 링크: myungchisa.com/seminars/${seminarId}?coupon=${shareCode}`] : []),
        ]}
      />

      {/* 열릴 때만 렌더 — 항상 떠 있는 dialog 는 스와이프 뒤로가기를 막는다 */}
      {dialogOpen && (
        <InstructorCouponDialog
          open={dialogOpen}
          seminarId={seminarId}
          listPrice={listPrice}
          recruitmentEnd={recruitmentEnd}
          editing={editing}
          editingDraft={editingDraft}
          takenCodes={draftMode
            ? drafts.filter((d: CouponDraft) => d.key !== editingDraft?.key).map((d: CouponDraft) => d.payload.code ?? '')
            : [
              ...drafts.filter((d: CouponDraft) => d.key !== editingDraft?.key).map((d: CouponDraft) => d.payload.code ?? ''),
              ...coupons.filter((c: InstructorCoupon) => c.id !== editing?.id && c.mine !== false).map((c: InstructorCoupon) => c.code),
            ]}
          defer={!draftMode && deferred}
          // 보류 고치기를 다시 열어도 '끝나는 날을 바꿨는지'는 서버에 저장된 원래 값과 비교한다
          originalEndsAt={editing ? coupons.find((c: InstructorCoupon) => c.id === editing.id)?.endsAt ?? null : undefined}
          onDeferredUpdate={savePendingUpdate}
          onClose={() => { setDialogOpen(false); setEditingDraft(null); }}
          onSaved={() => { setDialogOpen(false); void load(); }}
          onDraftSaved={saveDraft}
        />
      )}
    </Wrap>
  );
};

export default InstructorCouponSection;
